import type { FC } from 'react';
import type { Point } from '@/types';

/** 
 * Radius of the circular cutout at the hand base.
 * This determines the closest visible distance from center.
 * Must be larger than the offset to create visible concave curve.
 * The curve depth = CUTOUT_RADIUS - offset
 */
const CUTOUT_RADIUS = 18;

export interface ClockHandProps {
  /** Center point of the clock */
  center: Point;
  /** Rotation angle in degrees (0° = 12 o'clock, clockwise) */
  angle: number;
  /** Length of the hand in pixels */
  length: number;
  /** Width/thickness of the hand in pixels */
  width: number;
  /** Gap between hand base and center point in pixels */
  offset: number;
}

/**
 * A single clock hand rendered as a CSS-styled div.
 * Positioned at center, rotated by angle, with specified length and width.
 * The offset creates a gap between the hand base and the center point.
 * A CSS mask creates a concave curve at the base, appearing to wrap around the center.
 */
export const ClockHand: FC<ClockHandProps> = ({
  center,
  angle,
  length,
  width,
  offset,
}) => {
  // Extend transform-origin below the element by offset amount
  // This keeps the rotation pivot at the clock center while
  // the visible hand starts 'offset' pixels away from center
  
  // Mask creates a circular cutout at the clock center (pivot point)
  // This gives the hand base a concave curve as if wrapping around the center
  const maskImage = `radial-gradient(
    circle at 50% calc(100% + ${offset}px),
    transparent ${CUTOUT_RADIUS}px,
    black ${CUTOUT_RADIUS}px
  )`;

  return (
    <div
      className="clock-hand"
      style={{
        position: 'absolute',
        left: center.x,
        top: center.y,
        width: width,
        height: length,
        backgroundColor: '#ffffff',
        borderRadius: 9999,
        transformOrigin: `center calc(100% + ${offset}px)`,
        transform: `translateX(-50%) translateY(calc(-100% - ${offset}px)) rotate(${angle}deg)`,
        maskImage: maskImage,
        WebkitMaskImage: maskImage,
        willChange: 'transform',
      }}
    />
  );
};
